import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useMessageNotifications } from "@/hooks/use-message-notifications";
import { GlassButton } from "@/components/ui/glass-button";
import { StyledBadge } from "@/components/ui/styled-badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LogOut, MessageCircle, ChevronDown, Lock, Settings, ArrowLeft } from "lucide-react";
import { Link, useLocation } from "wouter";
import { ResetPasswordModal } from "@/components/reset-password-modal";
import { SettingsModal } from "@/components/settings-modal";
import { Button } from "@/components/ui/button";

interface AppHeaderProps {
  showMessageNotification?: boolean;
  username?: string;
  backUrl?: string;
}

export function AppHeader({ showMessageNotification = false, username, backUrl }: AppHeaderProps) {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  
  // Only use message notifications when in a jam night context
  let hasNewMessages = false;
  if (showMessageNotification) {
    try {
      const { hasNewMessages: hasNew } = useMessageNotifications();
      hasNewMessages = hasNew;
    } catch (error) {
      // Context not available, ignore
    }
  }

  return (
    <header className={`sticky-header-ios z-50 backdrop-blur-xl border-b border-white/20 transition-all duration-200 ${hasNewMessages ? 'bg-red-100/40' : 'bg-white/30'}`}
      style={{
        boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
      }}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {backUrl ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLocation(backUrl)}
                className="flex items-center gap-2 px-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            ) : (
              <Link href="/" className="flex items-center gap-2 text-current no-underline group">
                  <img src="/jam-jar.png" alt="Jam jar" className="h-6 w-6 drop-shadow-sm transition-transform duration-200 group-hover:scale-110" />
                  <h1 className="text-xl font-semibold text-slate-900/90 drop-shadow-sm">JamControl</h1>
              </Link>
            )}
            {user?.role === 'god' && (
              <StyledBadge variant="godMode">
                God Mode
              </StyledBadge>
            )}
          </div>

          {user && (
            <div className="flex items-center gap-3">
              {showMessageNotification && hasNewMessages && (
                <div className="relative">
                  <MessageCircle className="h-5 w-5 text-blue-600 drop-shadow-sm" />
                  <StyledBadge
                    variant="messageNotification"
                    className="absolute -top-2 -right-2 h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs"
                  >
                    !
                  </StyledBadge>
                </div>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <GlassButton
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4 drop-shadow-sm" />
                    {username && user.role !== 'god' ? username : 'Account'}
                    <ChevronDown className="h-3 w-3 drop-shadow-sm" />
                  </GlassButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsResetPasswordModalOpen(true)}>
                    <Lock className="mr-2 h-4 w-4" />
                    <span>Reset Password</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsSettingsModalOpen(true)}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
      
      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onOpenChange={setIsResetPasswordModalOpen}
        isOwnPassword={true}
      />

      <SettingsModal
        isOpen={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
      />
    </header>
  );
}
